/**
 * Tiny live waveform meter for the recording action bar.
 *
 * Reads frequency-band amplitudes (5 floats, 0..1) from useRecorder and
 * renders them as 5 vertical bars that scale with the live mic input.
 * Purely cosmetic — its job is "yes, your phone is hearing sound" so the
 * interviewer never has to wonder mid-take whether the mic is working.
 *
 * Bars use CSS transform: scaleY which the browser GPU-composes — much
 * cheaper than animating height/top, and stays smooth on a phone.
 */
interface Props {
  /** Per-band 0..1 amplitudes from useRecorder.audioLevels. */
  levels: number[];
  /** Whether the recorder is actively running. When false the bars
   *  collapse to a flat baseline regardless of `levels`. */
  active: boolean;
  className?: string;
}

const BAR_COUNT = 5;
// Minimum visible bar height so the meter doesn't fully disappear during
// a quiet moment — reads as "ready and listening" rather than "broken".
const MIN_SCALE = 0.08;

export default function WaveformIndicator({ levels, active, className }: Props) {
  return (
    <div
      className={
        'flex items-center justify-center gap-[3px] h-6 ' + (className ?? '')
      }
      aria-hidden
    >
      {Array.from({ length: BAR_COUNT }, (_, i) => {
        const raw = active ? (levels[i] ?? 0) : 0;
        const scale = active ? Math.max(MIN_SCALE, raw) : MIN_SCALE;
        return (
          <span
            key={i}
            className={
              'block w-[3px] rounded-full origin-center ' +
              (active ? 'bg-accent' : 'bg-faint/50') +
              // 90 ms keeps the bars feeling tight to the audio without
              // looking strobe-y. transition-transform stays GPU-cheap.
              ' transition-transform duration-[90ms] ease-out'
            }
            style={{
              height: '100%',
              transform: `scaleY(${scale.toFixed(3)})`,
            }}
          />
        );
      })}
    </div>
  );
}
